import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import ReactDatePicker from "react-datepicker";
import ErrorStateTable from "../../Components/ErrorStateTable";
import {
  clearCreateDocumentExcelTransaction,
  clearDocumentExcelAccUpload,
  clearDocumentExcelUploadStatus,
  deleteDraftAccTransactionImport,
  editDraftAccTransactionImport,
  requestCreateDocumentExcelTransaction,
  requestDocumentExcelAccTransactionUpload,
  requestListAccount,
  requestListBusiness,
  setAccTransactionInitialData,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  usePeriodLock,
} from "../../Context";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../Components/Button";
import {
  buildPeriodClosedRows,
  classNames,
  convertToTitleCase,
  getAccountingFileName,
  getErrorMessages,
  getPeriodClosedError,
  getPeriodClosedRows,
} from "../../Utils";
import PeriodLockBanner from "../../Components/v2/Accounting/PeriodLockBanner";
import PeriodClosedNotice from "../../Components/v2/Accounting/PeriodClosedNotice";
import PeriodRowNote from "../../Components/v2/Accounting/PeriodRowNote";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import {
  CheckIcon,
  DocumentIcon,
  PencilAltIcon,
  SelectorIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { Modal } from "../../Components/Modal";
import { ModalConfirm } from "../../Components/ModalConfirm";
import dayjs from "dayjs";
import { NumericFormat } from "react-number-format";
import { downloadData, getUrl } from "aws-amplify/storage";
import { ModalAlert } from "../../Components/ModalAlert";
import FormLabel from "../../Components/FormLabel";
import JournalEntryTable from "../../Components/JournalEntryTable";
import { TRANSACTION_TYPES } from "../../Constants";

const requirementImports = [
  { label: "hasHeader", value: 1 },
  { label: "maxColumns", value: 2 },
  { label: "maxRows", value: 3 },
  { label: "aiReviewNotice", value: 4 },
];

const AccountingTransactionImport = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const businessIdParam = searchParams.get("id");
  const businessDispatch = useBusinessTransactionDispatch();
  const accountingDispatch = useAccountingDispatch();

  const { user } = useAuthState();
  const { businesses, loading: businessLoading } =
    useBusinessTransactionState();
  const {
    uploadError,
    progressUpload,
    storageRequest,
    uploadLoading,
    uploadSuccess,
    excelUpload,
    excelUploadError,
    excelUploadClosedRows,
    excelUploadClosedThrough,
    excelTransactionsLoading,
    excelTransactionsError,
    excelTransactionsSuccess,
    excelTransactions,
    accounts,
    allAccountLoading,
  } = useAccountingState();

  const [showProgressModal, setShowProgressModal] = useState(false);
  const [modal, setModal] = useState({
    visible: false,
    title: "",
    description: "",
    type: "",
  });
  const [showConfirmModal, setshowConfirmModal] = useState(false);
  const [showEditModal, setshowEditModal] = useState(false);
  const [accountingTransactiontId, setaccountingTransactiontId] = useState("");
  const [accountItems, setaccountItems] = useState([
    {
      account: "",
      debit: { value: "0", floatValue: 0 },
      credit: { value: "0", floatValue: 0 },
    },
  ]);
  const [date, setdate] = useState(new Date());
  const [type, settype] = useState(null);
  const [note, setnote] = useState("");
  const isFormSubmit = useRef(false);
  const [inputErrors, setinputErrors] = useState({});
  const [accountItemsError, setaccountItemsError] = useState("");

  const [fieldErrorFile, setfieldErrorFile] = useState({
    status: false,
    message: "",
  });
  const [selectedBusiness, setselectedBusiness] = useState({});
  const [selectedFile, setselectedFile] = useState("");

  const [downloading, setdownloading] = useState(false);
  const [downloadProgress, setdownloadProgress] = useState(0);
  const [downloadDocError, setdownloadDocError] = useState(null);
  const [showDownloadErrorModal, setshowDownloadErrorModal] = useState(false);
  const downloadRef = useRef(null);

  const [showRequirementModal, setshowRequirementModal] = useState(false);
  const [requirements, setRequirements] = useState([]);

  const transactionTypes = useMemo(() => {
    // System-generated journals are posted by the backend, not entered by hand.
    const selectableTransactionTypes = TRANSACTION_TYPES.filter(
      (item) => !item.systemGenerated
    );

    return selectableTransactionTypes.map((item) => ({
      ...item,
      label: t(item.label),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TRANSACTION_TYPES, t]);

  const { refetch: refetchPeriod } = usePeriodLock(selectedBusiness?.id);

  // A spreadsheet has no date picker, so this is the one write that cannot be
  // prevented at the input — the per-row rejection *is* the interface. Both
  // passes are merged: the upload/parse call marks rows on arrival, the bulk
  // create call marks them again when it refuses to save.
  //
  // Computed above the effects rather than beside the render because the modal
  // effect below reads periodRejection in its dependency array, which is
  // evaluated during render.
  const periodRows = useMemo(
    () => ({
      // /upload answers 200: the row list is on the body, the wording under
      // error.transactions[i].created_at.
      ...buildPeriodClosedRows(excelUploadClosedRows, excelUploadError),
      // /bulk throws a 400: same two halves, still inside the HTTP envelope.
      ...getPeriodClosedRows(excelTransactionsError),
    }),
    [excelUploadClosedRows, excelUploadError, excelTransactionsError],
  );
  // Memoised on the error itself: this object goes into a dependency array, and
  // a fresh identity every render would re-fire the modal effect in a loop.
  const periodRejection = useMemo(
    () => getPeriodClosedError(excelTransactionsError),
    [excelTransactionsError],
  );

  useEffect(() => {
    return () => {
      clearDocumentExcelUploadStatus(accountingDispatch);
      clearCreateDocumentExcelTransaction(accountingDispatch);
    };
  }, [accountingDispatch]);

  useEffect(() => {
    if (!businesses.length && user.id) {
      requestListBusiness(businessDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessDispatch, businesses.length, user.id]);

  useEffect(() => {
    if (businessIdParam && businesses.length) {
      const business = businesses.find(
        (item) => item.value === businessIdParam
      );
      if (business) {
        setselectedBusiness({
          ...business,
          label: business.name,
          value: business.id,
        });
      }
    }
  }, [businessIdParam, businesses]);

  useEffect(() => {
    if (uploadSuccess) {
      setShowProgressModal(false);
    } else if (uploadError) {
      setShowProgressModal(false);
    }
  }, [uploadSuccess, uploadError]);

  useEffect(() => {
    if (fieldErrorFile.status) {
      setModal({
        visible: true,
        title: t("File Error"),
        description: t(fieldErrorFile.message),
        type: "WARNING",
      });
    }
  }, [fieldErrorFile, t]);

  useEffect(() => {
    if (excelTransactionsSuccess) {
      setModal({
        visible: true,
        title: t("Success"),
        description: t("Your transaction has been created."),
        type: "SUCCESS",
      });
    } else if (excelTransactionsError && !periodRejection) {
      // A closed period is a status, not a failure: it gets the amber notice
      // above the table and the per-row marks, never a red alert modal.
      setModal({
        visible: true,
        title: t("Error"),
        description: t(getErrorMessages(excelTransactionsError)),
        type: "ALERT",
      });
    }
  }, [excelTransactionsSuccess, excelTransactionsError, periodRejection, t]);

  // The upload answers 200, so PeriodClosedNotice never sees it and the cached
  // boundary would stay stale until something else refetched. The server just
  // told us where the boundary is; follow it so the banner agrees with the rows.
  useEffect(() => {
    if (!excelUploadClosedThrough) return;
    refetchPeriod();
  }, [excelUploadClosedThrough, refetchPeriod]);

  useEffect(() => {
    if (excelUpload?.transactions?.length > 0 && selectedBusiness?.id) {
      requestListAccount(accountingDispatch, {
        businessID: selectedBusiness.id,
        loadAll: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excelUpload?.transactions, selectedBusiness?.id]);

  useEffect(() => {
    setRequirements(requirementImports);
  }, []);

  const validateImport = () => {
    return requirements.every((requirement) => requirement.checked);
  };

  const handleRequirementChange = (index) => {
    if (index === -1) {
      let tempRequirements = requirements.map((requirement) => {
        return { ...requirement, checked: !requirement.checked };
      });
      setRequirements(tempRequirements);
    } else {
      let tempRequirements = requirements.map((requirement, i) =>
        i === index
          ? { ...requirement, checked: !requirement.checked }
          : requirement
      );
      setRequirements(tempRequirements);
    }
  };

  const handleCloseRequirementModal = () => {
    setRequirements((prevRequirements) =>
      prevRequirements.map((requirement) => ({
        ...requirement,
        checked: false,
      }))
    );
    setshowRequirementModal(false);
  };

  const renderRequirementModal = () => {
    return (
      <Modal
        title="Requirements"
        show={showRequirementModal}
        onClose={handleCloseRequirementModal}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button
              type="submit"
              title="Continue"
              disabled={!validateImport()}
              onClick={() => {
                handleImportClick(selectedFile);
                setshowRequirementModal(false);
              }}
            />
          </div>
        }
      >
        <div className="flex flex-col p-4">
          <fieldset className="space-y-5 text-base">
            {requirements.map((requirement, index) => (
              <div className="flex items-center" key={index}>
                <input
                  id={`requirements-${index}`}
                  aria-describedby="requirements-description"
                  type="checkbox"
                  className="focus:ring-blue-500 h-5 w-5 text-blue-600 border-gray-400 rounded mr-3"
                  onChange={() => handleRequirementChange(index)}
                  checked={requirement.checked || false}
                />
                <label
                  htmlFor={`requirements-${index}`}
                  className="text-sm text-gray-700"
                >
                  {t(requirement.label)}
                </label>
              </div>
            ))}
            <div className="flex items-center">
              <input
                id="check-all"
                name="check-all"
                type="checkbox"
                className="focus:ring-blue-500 h-5 w-5 text-blue-600 border-gray-400 rounded mr-3"
                onChange={() => handleRequirementChange(-1)}
                checked={
                  requirements.filter(
                    (requirement) => requirement?.checked !== true
                  ).length < 1
                }
              />
              <label
                htmlFor="check-all"
                className="text-sm font-medium text-gray-700"
              >
                {t("checkAll")}
              </label>
            </div>
          </fieldset>
        </div>
      </Modal>
    );
  };

  const handleFileUpload = (event) => {
    event.preventDefault();
    const file = event.dataTransfer
      ? event.dataTransfer.files[0]
      : event.target.files[0];
    const maxSize = 100 * 1024; // 100 KB

    if (
      file &&
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" &&
      file.size <= maxSize &&
      file.name.length < 120
    ) {
      setfieldErrorFile({ status: false, message: "" });
      setselectedFile(file);
    } else {
      if (!file) {
        setfieldErrorFile({ status: true, message: "Please select a file." });
      } else if (
        file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        setfieldErrorFile({
          status: true,
          message: "The file format must be Excel.",
        });
        event.target.value = "";
      } else if (file.size >= maxSize) {
        setfieldErrorFile({
          status: true,
          message: "File size should not exceed 100 KB",
        });
        event.target.value = "";
      } else if (file.name.length >= 120) {
        setfieldErrorFile({
          status: true,
          message: "The file name is too long, maximum 120 characters.",
        });
        event.target.value = "";
      }
    }
  };

  const handleImportClick = (selectedFile) => {
    if (!selectedFile) {
      setfieldErrorFile({
        status: true,
        message: "Select a file before clicking Upload.",
      });
      return;
    }

    if (!selectedBusiness.id) {
      setfieldErrorFile({
        status: true,
        message: "Business name is required.",
      });
      return;
    }

    setShowProgressModal(true);
    requestDocumentExcelAccTransactionUpload(accountingDispatch, {
      businessID: selectedBusiness.id,
      file: selectedFile,
    });
  };

  const renderProgressBar = (label, percentage) => (
    <div className="flex-1 overflow-hidden">
      <div className="w-full">
        <p className="text-xs text-slate-500">{t(label)}</p>
        <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
          <div
            className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
            style={{ width: `${percentage}%` }}
          >
            {percentage}%
          </div>
        </div>
      </div>
    </div>
  );

  const renderProgressModal = () => (
    <Modal
      title="Excel Document Upload Process"
      show={showProgressModal}
      onClose={() => {
        setShowProgressModal(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          {uploadLoading
            ? storageRequest !== null
              ? renderProgressBar(
                  "Processing",
                  Math.round(
                    (progressUpload?.transferredBytes /
                      progressUpload?.totalBytes) *
                      100
                  ),
                  100
                )
              : renderProgressBar("Uploading Excel Document File", 100)
            : null}
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex flex-col justify-center items-center">
          <DocumentIcon className="h-24 w-24 text-slate-500" />
          <div className="w-full text-center">
            <p className="text-xs text-slate-500 truncate">
              {selectedFile?.name}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );

  const onCreate = () => {
    const transactions = excelUpload?.transactions?.map((item) => {
      const total = item.accounts.reduce(
        (sum, account) => sum + account.debit,
        0
      );
      const accounts = item.accounts.map(({ id, credit, debit }) => ({
        id,
        credit,
        debit,
      }));

      return {
        created_at: item.createdAt,
        type: item.type,
        description: item.description,
        total,
        accounts,
      };
    });

    requestCreateDocumentExcelTransaction(accountingDispatch, {
      businessID: selectedBusiness.id,
      transactions,
    });
  };

  const handleModalConfirm = () => {
    if (excelTransactionsSuccess && excelTransactions?.length > 0) {
      const { startDate, endDate } = excelTransactions.reduce(
        (acc, transaction) => {
          const currentDate = new Date(transaction.createdAt);
          if (currentDate < acc.startDate) acc.startDate = currentDate;
          if (currentDate > acc.endDate) acc.endDate = currentDate;
          return acc;
        },
        {
          startDate: new Date(excelTransactions[0].createdAt),
          endDate: new Date(excelTransactions[0].createdAt),
        }
      );

      setAccTransactionInitialData(accountingDispatch, {
        transactionInitialData: {
          dateFilter: "OTHER",
          startDate: startDate,
          endDate: endDate,
        },
      });
      navigate("/v2/accounting");
      //clearCreateDocumentExcelTransaction(accountingDispatch);
      clearDocumentExcelAccUpload(accountingDispatch);
    } else {
      setModal((prevModal) => ({
        ...prevModal,
        visible: false,
      }));
      setfieldErrorFile({ status: false, message: "" });
    }
  };

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    if (!totalBytes) {
      setdownloadProgress(0);
    } else {
      setdownloadProgress(transferredBytes / totalBytes);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setdownloading(true);

      const request = downloadData({
        path: `public/${key}`,
        options: {
          onProgress: onDownloadProgress,
        },
      });

      downloadRef.current = request;
      const result = await request.result;
      const fileName = getAccountingFileName(key);

      downloadBlob(result.body, fileName);
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key);
      }
    } finally {
      setdownloading(false);
      setdownloadProgress(0);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: `public/${key}`,
        options: {
          useAccelerateEndpoint: true,
        },
      });
      const signedURL = urlResult.url.href;
      window.open(signedURL);
    } catch (signedURLError) {
      setdownloadDocError(signedURLError);
    }
  };

  const renderFileModal = () => (
    <Modal
      title="Download File"
      show={downloading}
      onClose={() => {
        if (downloadRef.current) {
          const downloadTask = downloadRef.current;
          downloadTask.cancel();
        }
        setdownloading(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <div className="flex-1 overflow-hidden">
            <div className="w-full">
              <p className="text-xs text-slate-500">{t("Downloading")}</p>
              <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
                  style={{
                    width: `${Math.round(downloadProgress * 100)}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex justify-center">
          <DocumentIcon className="h-24 w-24 text-slate-500" />
        </div>
      </div>
    </Modal>
  );

  const renderDownloadError = () => {
    if (downloadDocError) {
      return (
        <ModalConfirm
          open={showDownloadErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setshowDownloadErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(downloadDocError)}
          onConfirm={() => {
            setdownloadDocError(null);
          }}
        />
      );
    }
    return null;
  };

  const onDelete = () => {
    setshowConfirmModal(false);
    setaccountingTransactiontId("");
    deleteDraftAccTransactionImport(accountingDispatch, {
      accountingTransactiontId,
    });
  };

  const handleAddRow = () => {
    if (accountItems.length < 100) {
      setaccountItems([
        ...accountItems,
        {
          account: "",
          debit: { value: "0", floatValue: 0 },
          credit: { value: "0", floatValue: 0 },
        },
      ]);
    }
  };

  const handleRemoveRow = (index) => {
    const newAccountItems = [...accountItems];
    newAccountItems.splice(index, 1);
    setaccountItems(newAccountItems);
  };

  const updateAccountItem = (index, updatedProperties) => {
    return (prevState) => {
      const updatedItems = [...prevState];

      updatedItems[index] = {
        ...updatedItems[index],
        ...updatedProperties,
      };

      return updatedItems;
    };
  };

  const validateForm = () => {
    let hasErrors = false;
    const validationErrors = {};

    const validateRequiredField = (fieldName, fieldValue, displayFieldName) => {
      if (!fieldValue) {
        validationErrors[fieldName] = `${displayFieldName} is required.`;
        hasErrors = true;
      }
    };

    validateRequiredField("transactionType", type, "Transaction type");

    validateRequiredField("date", date, "Date");

    const validateAccount = accountItems.every((entry) => {
      return entry.account.value;
    });
    const validateDebitAmount = accountItems.every((entry) => {
      return entry.debit.floatValue !== undefined;
    });
    const validateCreditAmount = accountItems.every((entry) => {
      return entry.credit.floatValue !== undefined;
    });

    validateRequiredField("accountItems", validateAccount, "Account");
    validateRequiredField("debitAmount", validateDebitAmount, "Debit amount");
    validateRequiredField(
      "creditAmount",
      validateCreditAmount,
      "Credit amount"
    );

    for (const entry of accountItems) {
      const debit = entry.debit.floatValue;
      const credit = entry.credit.floatValue;

      if (!((debit !== 0 && credit === 0) || (debit === 0 && credit !== 0))) {
        validationErrors["debitCredit"] =
          "Debit and Credit amounts are invalid.";
        setinputErrors(validationErrors);
        return;
      }
    }

    const encounteredIds = new Set();
    const totalDebit = accountItems.reduce((acc, { account, debit }) => {
      if (encounteredIds.has(account.value)) {
        setaccountItemsError("Account cannot be the same.");
        hasErrors = true;
      } else {
        encounteredIds.add(account.value);
      }
      return acc + debit.floatValue;
    }, 0);

    const totalCredit = accountItems.reduce(
      (acc, { credit }) => acc + credit.floatValue,
      0
    );

    if (totalDebit !== totalCredit) {
      setaccountItemsError("Total debit and credit must be the same.");
      hasErrors = true;
    }

    setinputErrors(validationErrors);
    return !hasErrors;
  };

  const onSaveEditDraft = () => {
    isFormSubmit.current = true;

    if (validateForm()) {
      const accountInputs = accountItems.map(({ account, debit, credit }) => ({
        ...account,
        debit: debit.floatValue,
        credit: credit.floatValue,
      }));

      const input = {
        id: accountingTransactiontId,
        type: type.value,
        createdAt: date.toISOString(),
        description: note || "",
        accounts: accountInputs,
      };
      editDraftAccTransactionImport(accountingDispatch, {
        draftItem: input,
      });
      setshowEditModal(false);
      setinputErrors({});
      setaccountingTransactiontId("");
      setaccountItemsError("");
    }
  };

  const renderEditModal = () => {
    return (
      <Modal
        show={showEditModal}
        onClose={() => {
          setshowEditModal(false);
          setinputErrors({});
          setaccountingTransactiontId("");
          setaccountItemsError("");
        }}
        title={t("Change Accounting Account")}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button onClick={onSaveEditDraft} type="submit" title="Save" />
          </div>
        }
      >
        <div className="flex flex-col gap-4 p-4 text-sm text-gray-700">
          <div>
            <FormLabel htmlFor="type" label={t("Transaction Type")} required />
            <Select
              name="type"
              className="mt-1 text-sm"
              classNamePrefix="select2-selection"
              options={transactionTypes}
              placeholder={t("Select Transaction Type")}
              value={type}
              onChange={settype}
              maxMenuHeight={150}
            />
            {inputErrors.transactionType && (
              <div className="mt-1 text-red-600 text-sm">
                {t(inputErrors.transactionType)}
              </div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="date" label={t("Date")} required />
            <ReactDatePicker
              name="date"
              className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              selected={date}
              onChange={setdate}
              placeholderText={t("Select Date")}
              dateFormat="dd/MM/yyyy"
            />
            {inputErrors.date && (
              <div className="mt-1 text-red-600 text-sm">
                {t(inputErrors.date)}
              </div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="note" label={t("Note")} />
            <input
              type="text"
              name="note"
              value={note}
              placeholder={t("Enter Transaction Note")}
              onChange={(e) => setnote(e.target.value)}
              className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <JournalEntryTable
              accountItems={accountItems}
              setaccountItems={setaccountItems}
              accounts={accounts}
              allAccountLoading={allAccountLoading}
              inputErrors={inputErrors}
              isFormSubmit={isFormSubmit}
              updateAccountItem={updateAccountItem}
              handleRemoveRow={handleRemoveRow}
              handleAddRow={handleAddRow}
              selectedBusiness={selectedBusiness}
              businessIdParam={businessIdParam}
              accountingDispatch={accountingDispatch}
              requestListAccount={requestListAccount}
              editModal={true}
              accountItemsError={accountItemsError}
            />
          </div>
        </div>
      </Modal>
    );
  };

  function handleDragOver(event) {
    event.preventDefault();
  }

  function handleDragLeave(event) {
    event.preventDefault();
  }

  function hasInvalidTransactions(transactions) {
    return transactions?.some(
      ({ accounts = [], createdAt }) =>
        !createdAt || accounts.some(({ name }) => !name)
    );
  }

  const parsedErrorResponse = excelTransactionsError?.response?.body
    ? JSON.parse(excelTransactionsError.response.body)
    : null;

  const newExcelTransactionsError = parsedErrorResponse?.error;

  return (
    <div className="flex flex-col gap-6">
      {renderProgressModal()}
      {renderDownloadError()}
      {renderFileModal()}
      {renderEditModal()}
      {renderRequirementModal()}
      <ModalAlert
        open={showConfirmModal}
        setOpen={(open) => {
          setshowConfirmModal(open);
        }}
        title="deleteDraftTransactionTitle"
        description="accountingTransactionDescription"
        confirmButtonText="Yes"
        cancelButtonText="Cancel"
        onConfirm={onDelete}
      />
      <ModalConfirm
        open={modal.visible}
        setOpen={(open) => {
          setModal((prevModal) => ({
            ...prevModal,
            visible: open,
          }));
        }}
        title={modal.title}
        description={modal.description}
        type={modal.type}
        onConfirm={() => handleModalConfirm()}
      />
      <div className="flex sm:flex-row flex-col justify-between gap-4">
        <div className="flex sm:flex-row flex-col gap-4">
          <div>
            <Listbox
              disabled={excelUpload?.transactions?.length > 0}
              value={selectedBusiness}
              onChange={(value) => {
                setselectedBusiness(value);
                clearDocumentExcelAccUpload(accountingDispatch);
                clearDocumentExcelUploadStatus(accountingDispatch);
              }}
            >
              {({ open }) => (
                <>
                  <ListboxButton
                    className={classNames(
                      businessLoading ||
                        businesses.length === 0 ||
                        excelUpload?.transactions?.length > 0
                        ? "bg-gray-200 border-transparent text-gray-500 cursor-not-allowed"
                        : "bg-white",
                      "flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm"
                    )}
                  >
                    <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                      {businesses.length === 0
                        ? t("No Business Profile Yet")
                        : selectedBusiness?.label || t("Select Business Name")}
                    </span>
                    {businessLoading ? (
                      <svg
                        role="status"
                        className="w-5 h-5 animate-spin text-white"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="#E5E7EB"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentColor"
                        />
                      </svg>
                    ) : (
                      <SelectorIcon className="w-5 h-5 text-gray-500" />
                    )}
                  </ListboxButton>
                  <Transition
                    show={open}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm sm:w-auto">
                      {businesses.map((business) => (
                        <ListboxOption
                          key={business.value}
                          className={({ focus }) =>
                            classNames(
                              focus
                                ? "text-white bg-blue-600"
                                : "text-gray-900",
                              "cursor-default select-none relative py-2 pl-3 pr-9"
                            )
                          }
                          value={business}
                        >
                          {({ selected, focus }) => (
                            <>
                              <div className="flex items-center justify-between">
                                <span
                                  className={classNames(
                                    selected ? "font-semibold" : "font-normal",
                                    "ml-3 mr-2 block truncate"
                                  )}
                                >
                                  {business.label}
                                </span>
                                {selected ? (
                                  <span
                                    className={classNames(
                                      focus ? "text-white" : "text-blue-600",
                                      "absolute inset-y-0 right-0 flex items-center pr-4"
                                    )}
                                  >
                                    <CheckIcon className="h-5 w-5" />
                                  </span>
                                ) : null}
                              </div>
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </>
              )}
            </Listbox>
          </div>
          {businesses?.length === 0 && (
            <div>
              <Button
                className="w-full md:w-auto"
                onClick={() => navigate("/business/create")}
                title="Create Business Profile"
              />
            </div>
          )}
          {!excelUpload?.transactions?.length && (
            <div>
              <Button
                outline
                className="w-full md:w-auto"
                onClick={() =>
                  downloadFile("template/accounting_transaction_template.xlsx")
                }
                title="Download Template"
              />
            </div>
          )}
        </div>
        <div className="flex sm:flex-row flex-col gap-4">
          {!excelUpload?.transactions?.length && (
            <div className="flex sm:flex-row flex-col gap-4">
              <div className="flex">
                <label
                  htmlFor="upload-input"
                  className="items-center whitespace-nowrap px-4 py-2 bg-gray-100 border-y border-l border-gray-300 rounded-l-md cursor-pointer text-sm text-gray-600 hover:bg-gray-200 focus:bg-blue-50 focus:outline-none disabled:opacity-25 transition "
                  onDragOver={handleDragOver}
                  onDrop={handleFileUpload}
                  onDragLeave={handleDragLeave}
                >
                  {t("Choose File")}
                  <input
                    type="file"
                    id="upload-input"
                    className="sr-only"
                    onChange={handleFileUpload}
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  />
                </label>
                <div
                  className="flex w-full bg-white border border-gray-300 rounded-r-md px-4 py-2 overflow-hidden"
                  onDragOver={handleDragOver}
                  onDrop={handleFileUpload}
                  onDragLeave={handleDragLeave}
                >
                  <span className="align-middle w-36 text-gray-400 text-sm truncate">
                    {selectedFile?.name || t("File not selected")}
                  </span>
                </div>
              </div>
              <div>
                <Button
                  outline
                  className="w-full md:w-auto"
                  onClick={() => handleImportClick(selectedFile)}
                  title={t("Upload")}
                />
              </div>
            </div>
          )}

          {((excelUploadError?.transactions &&
            hasInvalidTransactions(excelUpload?.transactions)) ||
            excelTransactionsError) && (
            <div>
              <Button
                outline
                disabled={excelTransactionsLoading}
                className="w-full md:w-auto"
                type="button"
                title={t("Change File")}
                onClick={() => {
                  clearDocumentExcelAccUpload(accountingDispatch);
                  clearCreateDocumentExcelTransaction(accountingDispatch);
                  setselectedFile("");
                }}
              />
            </div>
          )}

          <div>
            <Button
              className="w-full md:w-auto"
              type="button"
              disabled={
                !excelUpload?.transactions?.length ||
                (excelUploadError?.transactions &&
                  hasInvalidTransactions(excelUpload?.transactions)) ||
                excelTransactionsLoading ||
                excelTransactionsError
                // Marked period rows deliberately do NOT disable this. The
                // import is all-or-nothing and the server is the authority on
                // the boundary; the user presses save, /bulk refuses the batch,
                // and the notice explains it. Blocking here would strand anyone
                // whose cached boundary is out of date with no way to ask.
              }
              loading={excelTransactionsLoading}
              title={t("Create Accounting Transaction")}
              onClick={() => onCreate()}
            />
          </div>
        </div>
      </div>
      {/* States the boundary before a file is chosen — the only prevention a
          spreadsheet import can offer. */}
      <PeriodLockBanner businessID={selectedBusiness?.id} />
      {/* Once the save is refused: the server's own wording, and the refresh of
          the cached boundary. Before that the banner above already states the
          boundary and the date cells carry the per-row reasons, so there is no
          third amber box repeating "Periode sudah ditutup" with less detail. */}
      <PeriodClosedNotice
        error={excelTransactionsError}
        onRefresh={refetchPeriod}
      />
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Date")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Transaction Type")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Description")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Account")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Debit")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Credit")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Action")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {excelUpload?.transactions?.length > 0 ? (
                    excelUpload?.transactions?.map((item, index) => {
                      const transactionErrors =
                        excelUploadError?.transactions?.[index];
                      const createdAtError = transactionErrors?.created_at?.[0];
                      const newTransactionError =
                        newExcelTransactionsError?.transactions?.[index];
                      return (
                        <React.Fragment key={index}>
                          {item?.accounts?.map((account, accountIndex) => {
                            const accountErrors =
                              transactionErrors?.accounts?.[accountIndex];
                            const codeError = accountErrors?.code?.[0];
                            return (
                              <tr
                                key={`${index}-${accountIndex}`}
                                className={
                                  accountIndex > 0 ? "border-none" : ""
                                }
                              >
                                {accountIndex === 0 && (
                                  <>
                                    <td
                                      className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap"
                                      rowSpan={item.accounts.length}
                                    >
                                      {/* The old `&& !item.createdAt` guard hid
                                          the message whenever the date parsed
                                          fine — exactly the case for a row the
                                          server rejected on period, which has a
                                          perfectly valid date. Always render
                                          the date, and append the reason. */}
                                      {createdAtError && !item.createdAt ? (
                                        <span className="text-red-500">
                                          {createdAtError}
                                        </span>
                                      ) : (
                                        <>
                                          {dayjs(item.createdAt)
                                            .locale(i18n.language)
                                            .format("DD MMM YYYY")}
                                          {periodRows[index] !== undefined && (
                                            <PeriodRowNote
                                              message={periodRows[index]}
                                            />
                                          )}
                                        </>
                                      )}
                                    </td>
                                    <td
                                      className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap"
                                      rowSpan={item.accounts.length}
                                    >
                                      {t(convertToTitleCase(item.type))}
                                    </td>
                                    <td
                                      className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap"
                                      rowSpan={item.accounts.length}
                                    >
                                      {item.description}
                                    </td>
                                  </>
                                )}
                                <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap">
                                  {codeError && !account.name ? (
                                    <span className="text-red-500">
                                      {codeError}
                                    </span>
                                  ) : (
                                    `${account.name} - ${account.code}`
                                  )}
                                </td>
                                <td className="px-4 py-2 font-medium text-right text-sm text-gray-900 whitespace-nowrap">
                                  <NumericFormat
                                    displayType={"text"}
                                    value={account.debit}
                                    thousandSeparator={"."}
                                    decimalSeparator={","}
                                    prefix={"Rp"}
                                  />
                                </td>
                                <td className="px-4 py-2 font-medium text-right text-sm text-gray-900 whitespace-nowrap">
                                  <NumericFormat
                                    displayType={"text"}
                                    value={account.credit}
                                    thousandSeparator={"."}
                                    decimalSeparator={","}
                                    prefix={"Rp"}
                                  />
                                </td>
                                {accountIndex === 0 && (
                                  <td
                                    className="px-4 py-2 font-medium text-right text-sm text-gray-900 whitespace-nowrap"
                                    rowSpan={item.accounts.length}
                                  >
                                    <div className="flex flex-auto gap-2">
                                      {excelTransactionsLoading ? (
                                        <div className="w-5 h-5 bg-gray-300 rounded-md" />
                                      ) : (
                                        <button
                                          onClick={() => {
                                            const selectedType =
                                              transactionTypes.find(
                                                (type) =>
                                                  type.value === item.type
                                              );

                                            const mappedItems =
                                              item?.accounts?.map((item) => {
                                                return {
                                                  account: {
                                                    ...item,
                                                    label: `${item?.code} - ${item?.name}`,
                                                    value: item.id,
                                                  },
                                                  debit: {
                                                    value: `${item.debit}`,
                                                    floatValue: item.debit,
                                                  },
                                                  credit: {
                                                    value: `${item.credit}`,
                                                    floatValue: item.credit,
                                                  },
                                                };
                                              });
                                            setshowEditModal(true);
                                            settype(selectedType);
                                            setdate(new Date(item.createdAt));
                                            setnote(item.description);
                                            setaccountItems(mappedItems);
                                            setaccountingTransactiontId(
                                              item.id
                                            );
                                          }}
                                        >
                                          <PencilAltIcon className="h-5 w-5 text-blue-500" />
                                        </button>
                                      )}
                                      {excelTransactionsLoading ? (
                                        <div className="w-5 h-5 bg-gray-300 rounded-md" />
                                      ) : (
                                        <button
                                          onClick={() => {
                                            setshowConfirmModal(true);
                                            setaccountingTransactiontId(
                                              item.id
                                            );
                                          }}
                                        >
                                          <TrashIcon className="h-5 w-5 text-red-500" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                          {newTransactionError?.accounts?.[0]?.id && (
                            <tr className="text-red-500 border-none">
                              {[...Array(3)].map((_, index) => (
                                <td
                                  key={index}
                                  className="px-4 py-2 font-medium text-sm whitespace-nowrap"
                                />
                              ))}
                              <td className="px-4 py-2 font-medium text-sm whitespace-nowrap">
                                {newTransactionError.accounts[0].id}
                              </td>
                              {[...Array(3)].map((_, index) => (
                                <td
                                  key={index}
                                  className="px-4 py-2 font-medium text-sm whitespace-nowrap"
                                />
                              ))}
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <ErrorStateTable
                      colSpan={7}
                      error={uploadError}
                      emptyTitle="No import data"
                    />
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingTransactionImport;
